import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faSpinner, faStop, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { streamPlanner } from '../../services/agentsApi';
import type { PlannerMessage, PlannerEvent } from '../../types/agents';
import ToolCallCard from './ToolCallCard';
import MarkdownRenderer from '../common/MarkdownRenderer';

interface Props {
  validationId?: string | null;
  onAgentCreated?: () => void;
}

const WELCOME_MESSAGE = "Hi! I'm here to help you create a new AI agent. Tell me about what you need this agent to do, and I'll help you define it.";

/** Try to extract a validation ID from create_validation tool result text */
function extractValidationId(resultText?: string): string | null {
  if (!resultText) return null;
  const match = resultText.match(/ID:\s*([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

type ToolCall = { tool: string; args: any; result?: string };

/** Compact markdown bubble for chat messages */
const ChatMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <div className="planner-chat-md">
    <MarkdownRenderer content={content} className="text-sm" />
  </div>
);

const PlannerTab: React.FC<Props> = ({ validationId, onAgentCreated }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<PlannerMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [currentToolCalls, setCurrentToolCalls] = useState<ToolCall[]>([]);
  const [currentContent, setCurrentContent] = useState('');
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Refs to avoid stale closures in stream callbacks
  const contentRef = useRef('');
  const toolCallsRef = useRef<ToolCall[]>([]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentToolCalls, currentContent]);

  const handleSend = () => {
    if (!input.trim() || streaming) return;

    const userMessage = input.trim();
    setInput('');

    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setStreaming(true);
    setCurrentToolCalls([]);
    setCurrentContent('');
    contentRef.current = '';
    toolCallsRef.current = [];

    const history = messages.map((m) => ({ role: m.role, content: m.content }));

    abortRef.current = streamPlanner(
      validationId || null,
      userMessage,
      history,
      (event: PlannerEvent) => {
        if (event.type === 'tool_call') {
          const tc: ToolCall = { tool: event.tool!, args: event.args || {} };
          toolCallsRef.current = [...toolCallsRef.current, tc];
          setCurrentToolCalls([...toolCallsRef.current]);
        } else if (event.type === 'tool_result') {
          if (event.tool === 'create_validation') {
            const newId = extractValidationId(event.result);
            if (newId) {
              setCreatedAgentId(newId);
              onAgentCreated?.();
            }
          }
          if (event.tool === 'create_ticket') {
            onAgentCreated?.();
          }

          const updated = [...toolCallsRef.current];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].tool === event.tool && !updated[i].result) {
              updated[i] = { ...updated[i], result: event.result };
              break;
            }
          }
          toolCallsRef.current = updated;
          setCurrentToolCalls([...updated]);
        } else if (event.type === 'message') {
          contentRef.current = event.content || '';
          setCurrentContent(contentRef.current);
        }
      },
      (error: string) => {
        setStreaming(false);
        const finalContent = contentRef.current || `Error: ${error}`;
        const finalToolCalls = toolCallsRef.current;
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: finalContent,
            toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
          },
        ]);
        setCurrentToolCalls([]);
        setCurrentContent('');
        contentRef.current = '';
        toolCallsRef.current = [];
      },
      () => {
        setStreaming(false);
        const finalContent = contentRef.current;
        const finalToolCalls = toolCallsRef.current;
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: finalContent,
            toolCalls: finalToolCalls.length > 0 ? [...finalToolCalls] : undefined,
          },
        ]);
        setCurrentToolCalls([]);
        setCurrentContent('');
        contentRef.current = '';
        toolCallsRef.current = [];
      },
    );
  };

  const handleStop = () => {
    abortRef.current?.();
    setStreaming(false);
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 400 }}>
      {/* Compact prose overrides for chat bubbles */}
      <style>{`
        .planner-chat-md .prose {
          font-size: 0.875rem;
          line-height: 1.625;
        }
        .planner-chat-md .prose p {
          margin-top: 0.125rem;
          margin-bottom: 0.375rem;
          line-height: 1.625;
          color: #374151;
        }
        .planner-chat-md .prose p:last-child {
          margin-bottom: 0;
        }
        .planner-chat-md .prose h1 {
          font-size: 1rem;
          margin-top: 0.5rem;
          margin-bottom: 0.25rem;
        }
        .planner-chat-md .prose h2 {
          font-size: 0.9375rem;
          margin-top: 0.5rem;
          margin-bottom: 0.25rem;
        }
        .planner-chat-md .prose h3,
        .planner-chat-md .prose h4 {
          font-size: 0.875rem;
          margin-top: 0.375rem;
          margin-bottom: 0.125rem;
        }
        .planner-chat-md .prose ul,
        .planner-chat-md .prose ol {
          margin-top: 0.25rem;
          margin-bottom: 0.375rem;
          padding-left: 0;
        }
        .planner-chat-md .prose li {
          margin-top: 0;
          margin-bottom: 0.125rem;
          margin-left: 0.25rem;
          line-height: 1.5;
          color: #374151;
        }
        .planner-chat-md .prose pre {
          margin-top: 0.375rem;
          margin-bottom: 0.375rem;
          font-size: 0.75rem;
          border-radius: 0.5rem;
        }
        .planner-chat-md .prose code {
          font-size: 0.8125rem;
        }
        .planner-chat-md .prose blockquote {
          margin-top: 0.375rem;
          margin-bottom: 0.375rem;
          padding: 0.25rem 0.75rem;
        }
        .planner-chat-md .prose table {
          margin-top: 0.375rem;
          margin-bottom: 0.375rem;
          font-size: 0.8125rem;
        }
        .planner-chat-md .prose hr {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .planner-chat-md .prose strong {
          color: #111827;
        }
        .planner-chat-md .prose img {
          margin-top: 0.375rem;
          margin-bottom: 0.375rem;
        }
      `}</style>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Welcome message */}
        {messages.length === 0 && !streaming && (
          <div className="flex gap-3">
            <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
              <p className="text-sm text-gray-800 leading-relaxed">{WELCOME_MESSAGE}</p>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
            <div
              className={`max-w-[85%] ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-2xl rounded-tr-md px-4 py-2.5'
                  : ''
              }`}
            >
              {msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="mb-2">
                  {msg.toolCalls.map((tc, j) => (
                    <ToolCallCard key={j} tool={tc.tool} args={tc.args} result={tc.result} />
                  ))}
                </div>
              )}
              {msg.content && (
                msg.role === 'assistant' ? (
                  <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3">
                    <ChatMarkdown content={msg.content} />
                  </div>
                ) : (
                  <div className="text-sm leading-relaxed">
                    {msg.content}
                  </div>
                )
              )}
            </div>
          </div>
        ))}

        {/* Streaming state */}
        {streaming && (
          <div className="flex gap-3">
            <div className="max-w-[85%]">
              {currentToolCalls.map((tc, j) => (
                <ToolCallCard key={j} tool={tc.tool} args={tc.args} result={tc.result} />
              ))}
              {currentContent && (
                <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3">
                  <ChatMarkdown content={currentContent} />
                </div>
              )}
              {!currentContent && currentToolCalls.length === 0 && (
                <div className="bg-gray-100 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  Thinking...
                </div>
              )}
            </div>
          </div>
        )}

        {/* "View Agent" card after workspace creation */}
        {createdAgentId && !streaming && (
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/consultant/agents/${createdAgentId}`)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm font-medium text-green-700 hover:bg-green-100 transition-colors"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
              View Agent Workspace
            </button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-100 px-5 py-4 bg-white">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Describe your agent requirements..."
            disabled={streaming}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 bg-gray-50 placeholder-gray-400"
          />
          {streaming ? (
            <button
              onClick={handleStop}
              className="w-10 h-10 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faStop} className="text-sm" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faArrowUp} className="text-sm" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlannerTab;
